import { posthogService } from "../../../services/posthogService";
import { taskService } from "../../../services/taskService";
import { useTaskOperations } from "../../../composables/useTaskOperations";

export function useTimeTrackerActions() {
  const { resetAllTasks, loadDefaultData } = useTaskOperations();

  function downloadTasks() {
    const tasks = localStorage.getItem("timeTracker_tasks");
    if (tasks) {
      const blob = new Blob([tasks], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timeTracker_tasks_${
        new Date().toISOString().split("T")[0]
      }.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // Track data export
      posthogService.trackDataExported();
    }
  }

  function handleFileUpload(
    file: File,
    _tasks: any[],
    updateTasks: (tasks: any[]) => void
  ) {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const importedTasks = JSON.parse(e.target?.result as string);
          if (Array.isArray(importedTasks)) {
            // Validate tasks before saving
            const validTasks = importedTasks.filter(
              (task) =>
                task.id !== undefined &&
                task.name &&
                task.topicId &&
                typeof task.duration === "number" &&
                typeof task.estimatedDuration === "number"
            );

            // Convert any epoch timestamps to ISO strings
            const migratedTasks = validTasks.map((task) => {
              // Migrate sessions if they use epoch timestamps
              if (task.sessions && task.sessions.length > 0) {
                task.sessions = task.sessions.map(
                  (session: {
                    startTime: number | string;
                    endTime?: number | string;
                  }) => {
                    // Check if startTime is a number (epoch timestamp)
                    if (typeof session.startTime === "number") {
                      return {
                        startTime: new Date(session.startTime).toISOString(),
                        endTime: session.endTime
                          ? new Date(session.endTime as number).toISOString()
                          : undefined,
                      };
                    }
                    return session;
                  }
                );
              }

              // Migrate createdAt if it's a number
              if (typeof task.createdAt === "number") {
                task.createdAt = new Date(task.createdAt).toISOString();
              } else if (!task.createdAt) {
                // Add createdAt if it doesn't exist
                task.createdAt = new Date().toISOString();
              }

              // Remove deprecated fields
              const { isActive, startTime, _manualDuration, ...cleanTask } =
                task;
              return cleanTask;
            });

            if (migratedTasks.length === importedTasks.length) {
              localStorage.setItem(
                "timeTracker_tasks",
                JSON.stringify(migratedTasks)
              );
              taskService.saveTasks(migratedTasks);
              // Update tasks (remove deprecated fields)
              const updatedTasks = migratedTasks.map((task) => {
                const { isActive, startTime, _manualDuration, ...cleanTask } =
                  task;
                return cleanTask;
              });
              updateTasks(updatedTasks);

              // Track data import
              posthogService.trackDataImported();

              alert("Tasks imported successfully!");
            } else {
              alert(
                "Some tasks were invalid and were skipped. Please check the file format."
              );
            }
          } else {
            alert(
              "Invalid file format. Please upload a valid JSON file containing an array of tasks."
            );
          }
        } catch (error) {
          alert("Error parsing JSON file. Please check the file format.");
        }
      };
      reader.readAsText(file);
    }
  }

  function handleResetAllTasks(
    updateTasks: (tasks: any[]) => void,
    stopTimer: () => void
  ) {
    const newTasks = resetAllTasks();
    updateTasks(newTasks);
    stopTimer();
  }

  function handleLoadDefaultData(
    updateTasks: (tasks: any[]) => void,
    stopTimer: () => void
  ) {
    const newTasks = loadDefaultData();
    updateTasks(newTasks);
    stopTimer();
  }

  return {
    downloadTasks,
    handleFileUpload,
    handleResetAllTasks,
    handleLoadDefaultData,
  };
}
